import React, { createContext, useCallback, useMemo, ReactNode } from 'react';
import startTracking from '../startTracking';
import { prodUrl, devUrl, testUrl } from '../constants';
import type {
  ExperimentEventOptions,
  MetaAttributeUpdater,
  TrackRenderedExperiment,
  WeapiEventPayload,
} from '../contracts';

const ANALYTICS_ENDPOINT = {
  production: prodUrl,
  prod: prodUrl,
  dev: devUrl,
  development: devUrl,
  test: testUrl,
};

const defaultContext = {
  trackEvent: (
    _eventName: string,
    _customAttributes?: object,
    _overrides?: object,
    _options?: {
      useFetch?: boolean;
      fetchOption?: RequestInit;
    },
  ) => {},
  trackRenderedExperiment: (
    _experimentKey: string,
    _bucketKey: string,
    _customAttributes?: Record<string, unknown>,
    _options?: ExperimentEventOptions,
  ) => {},
  getPayload: (_data: {
    eventName: string;
    customAttributes?: object;
    overrides?: object;
    options?: {
      useFetch?: boolean;
      fetchOption?: RequestInit;
    };
  }) => Promise.resolve({} as WeapiEventPayload),
  updateMetaAttributes: (_updater: MetaAttributeUpdater) => {},
};

const AnalyticsContext = createContext(defaultContext);

export type ContextType = typeof defaultContext;

type OwnProps = {
  children: ReactNode;
  environment: keyof typeof ANALYTICS_ENDPOINT;
  track: string;
  projectName: string;
  correlationId?: string;
  payloadMetaAttributes?: object;
  debug?: boolean;
};

export function AnalyticsProvider({
  children,
  environment,
  track,
  projectName,
  correlationId = '',
  payloadMetaAttributes = {},
  debug = true,
}: OwnProps) {
  const endpoint = ANALYTICS_ENDPOINT[environment] || ANALYTICS_ENDPOINT.dev;
  const isProdLikeEnv = environment === 'production' || environment === 'prod';

  const { trackData, getPayload, updateMetaAttributes } = useMemo(
    () =>
      startTracking(
        environment,
        endpoint,
        track,
        projectName,
        correlationId,
        payloadMetaAttributes,
        debug,
      ),
    [],
  );

  const trackEvent = useCallback(
    (
      eventName: string,
      customAttributes?: object,
      overrides?: object,
      options?: object,
    ) => {
      trackData({
        eventName,
        customAttributes: customAttributes || {},
        overrides: overrides || {},
        options: options || {},
      });
    },
    [trackData],
  );

  const trackRenderedExperiment: TrackRenderedExperiment = useCallback(
    (experimentKey, bucketKey, customAttributes, options) => {
      if (!experimentKey || !bucketKey) {
        if (!isProdLikeEnv) {
          console.error(
            'trackRenderedExperiment: experimentKey and bucketKey are required parameters. Check the Network tab (filter by "graph") to verify your events are being sent correctly.',
          );
        }
        return;
      }

      trackData({
        eventName: 'experiment-rendered',
        customAttributes: {
          experimentKey,
          bucketKey,
          ...(customAttributes || {}),
        },
        options: options || {},
      });
    },
    [trackData],
  );

  const value = useMemo(
    (): ContextType => ({
      trackEvent,
      trackRenderedExperiment,
      getPayload,
      updateMetaAttributes,
    }),
    [trackEvent, trackRenderedExperiment, getPayload, updateMetaAttributes],
  );

  return (
    <AnalyticsContext.Provider value={value}>
      {children}
    </AnalyticsContext.Provider>
  );
}

export default AnalyticsContext;
